import type { Socket } from 'socket.io-client';

const HEARTBEAT_MS = 20_000;

function isTrackerTabActive(): boolean {
  if (typeof document === 'undefined') return false;
  return document.visibilityState === 'visible' && document.hasFocus();
}

export function installActiveTabPresence(socket: Socket): () => void {
  let heartbeatTimer: number | null = null;
  let lastSentActive: boolean | null = null;

  const emitPresence = (active: boolean, force = false) => {
    if (!socket.connected) return;
    if (!force && lastSentActive === active) return;
    lastSentActive = active;
    socket.emit(active ? 'tracker:presence_active' : 'tracker:presence_idle', {
      at: new Date().toISOString(),
    });
  };

  const stopHeartbeat = () => {
    if (heartbeatTimer != null) {
      window.clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  const startHeartbeat = () => {
    if (heartbeatTimer != null) return;
    heartbeatTimer = window.setInterval(() => {
      if (isTrackerTabActive()) {
        emitPresence(true, true);
      } else {
        stopHeartbeat();
        emitPresence(false);
      }
    }, HEARTBEAT_MS);
  };

  const syncPresence = (force = false) => {
    const active = isTrackerTabActive();
    emitPresence(active, force);
    if (active) startHeartbeat();
    else stopHeartbeat();
  };

  const onActiveSignal = () => syncPresence();
  const onConnect = () => {
    lastSentActive = null;
    syncPresence(true);
  };
  const onDisconnect = () => {
    lastSentActive = null;
    stopHeartbeat();
  };

  window.addEventListener('focus', onActiveSignal);
  window.addEventListener('blur', onActiveSignal);
  window.addEventListener('pointerdown', onActiveSignal, { passive: true });
  window.addEventListener('keydown', onActiveSignal);
  document.addEventListener('visibilitychange', onActiveSignal);
  socket.on('connect', onConnect);
  socket.on('disconnect', onDisconnect);
  syncPresence(true);

  return () => {
    stopHeartbeat();
    if (socket.connected && lastSentActive === true) {
      socket.emit('tracker:presence_idle', { at: new Date().toISOString() });
    }
    window.removeEventListener('focus', onActiveSignal);
    window.removeEventListener('blur', onActiveSignal);
    window.removeEventListener('pointerdown', onActiveSignal);
    window.removeEventListener('keydown', onActiveSignal);
    document.removeEventListener('visibilitychange', onActiveSignal);
    socket.off('connect', onConnect);
    socket.off('disconnect', onDisconnect);
  };
}
